import { useQuery, type QueryKey } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { carryFilters } from "@/lib/filters";

import { getBreakout, getOverview, getRegion, getSummary, getUnits, type FreshnessMeta } from "./api";
import { ConversionPage } from "./ConversionPage";
import { filterFromSearch, queryFor } from "./controls";
import { SummaryPage } from "./SummaryPage";
import { TrafficPage } from "./TrafficPage";
import { UnitsPage } from "./UnitsPage";

/**
 * The freshness bridge (native-module contract step 2).
 *
 * `ModuleViewport` renders above this module's tab content, so the module
 * cannot hand its S8 `meta` up as a prop. Each tab reads its own page cache
 * entry, so the shell names the date of the response actually on screen and
 * no second request is made for the same JSON.
 */
type MetaResponse = { data: unknown; meta: FreshnessMeta };
type MetaQuery = { queryKey: QueryKey; queryFn: () => Promise<MetaResponse> };

export function metaQueryFor(tab: string | undefined, search: Record<string, unknown>): MetaQuery {
  const legacyRegion = tab === "uk" || tab === "eu" || tab === "mena" ? tab : null;
  const effectiveTab = legacyRegion !== null || tab === "overview" ? "units" : tab;
  let effectiveSearch = search;
  if (legacyRegion) effectiveSearch = { ...search, f_region: legacyRegion };
  else if (tab === "overview") {
    const { f_region: _legacyRegion, ...withoutRegion } = search;
    effectiveSearch = withoutRegion;
  }
  const filter = filterFromSearch(effectiveSearch, tab);
  const query = queryFor(filter);
  if (effectiveTab === "traffic" || effectiveTab === "conversion") {
    return {
      queryKey: ["meteoric_units_sold", effectiveTab, query] as const,
      queryFn: () => getBreakout(effectiveTab, query),
    };
  }
  if (effectiveTab === "units") {
    if (filter.product || filter.channel.length > 0 || filter.source) {
      return {
        queryKey: ["meteoric_units_sold", "units", query] as const,
        queryFn: () => getUnits(query),
      };
    }
    if (filter.region === "uk" || filter.region === "eu" || filter.region === "mena") {
      const region = filter.region;
      return {
        queryKey: ["meteoric_units_sold", "region", region] as const,
        queryFn: () => getRegion(region),
      };
    }
    return {
      queryKey: ["meteoric_units_sold", "overview"] as const,
      queryFn: getOverview,
    };
  }
  return {
    queryKey: ["meteoric_units_sold", "summary", query] as const,
    queryFn: () => getSummary(query),
  };
}

export function useUnitsSoldMeta(enabled: boolean): FreshnessMeta | null | undefined {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const params = useParams({ strict: false }) as { tab?: string };
  const request = metaQueryFor(params.tab, search);
  const head = useQuery<MetaResponse>({
    ...request,
    enabled,
  });
  return enabled ? head.data?.meta : undefined;
}

/**
 * Traffic to Units, rendered natively in the portal.
 *
 * `decision` and `ownerName` are deliberately not read: permission rendering
 * is shell-owned (I-7), and `ShellPage` already wraps this module in
 * `PermissionGate`. Reading either here would create a second renderer.
 */
export function UnitsSoldModule({ activeTabKey, areaName }: {
  decision: "edit" | "view" | "none";
  activeTabKey: string | null;
  areaName: string;
  ownerName?: string | null;
}) {
  const params = useParams({ strict: false }) as { tab?: string };
  const navigate = useNavigate();
  const lastRedirectedTab = useRef<string | null>(null);
  const legacy = params.tab === "uk" || params.tab === "eu" || params.tab === "mena" || params.tab === "overview";

  useEffect(() => {
    if (!legacy) {
      lastRedirectedTab.current = null;
      return;
    }
    if (!shouldRedirect(lastRedirectedTab.current, params.tab)) return;
    lastRedirectedTab.current = params.tab ?? null;
    void navigate({
      to: ".",
      params: { tab: "units" },
      search: (prev) => legacyRedirectSearch(params.tab, prev),
      replace: true,
    });
  }, [legacy, navigate, params.tab]);

  if (legacy) return <></>;
  if (activeTabKey === "traffic") return <TrafficPage areaName={areaName} />;
  if (activeTabKey === "conversion") return <ConversionPage areaName={areaName} />;
  if (activeTabKey === "units") return <UnitsPage areaName={areaName} />;
  return <SummaryPage areaName={areaName} />;
}

export function legacyRedirectSearch(tab: string | undefined, prev: Record<string, unknown>): Record<string, unknown> {
  const carried = carryFilters(prev);
  if (tab === "uk" || tab === "eu" || tab === "mena") carried.f_region = tab;
  else delete carried.f_region;
  return carried;
}

export function shouldRedirect(lastTab: string | null, tab: string | undefined): boolean {
  const legacy = tab === "uk" || tab === "eu" || tab === "mena" || tab === "overview";
  return legacy && lastTab !== tab;
}
